import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Tag, X } from "lucide-react";
import { useCart } from "@/context/CartContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const useCountdown = (until) => {
  const [left, setLeft] = useState(null);
  useEffect(() => {
    if (!until) return;
    const end = new Date(until).getTime();
    const tick = () => setLeft(Math.max(0, end - Date.now()));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [until]);
  return left;
};

const pad = (n) => String(n).padStart(2, "0");

export default function DealBanner() {
  const { applyPromo, promo } = useCart();
  const [deal, setDeal] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const applied = useRef(false);

  useEffect(() => {
    axios.get(`${API}/deal`).then(({ data }) => {
      if (data?.active) {
        setDeal(data);
        if (localStorage.getItem("mx_deal_dismissed") === `${data.code}:${data.until}`) setDismissed(true);
      }
    }).catch((err) => console.warn("deal fetch failed", err));
  }, []);

  const left = useCountdown(deal?.until);

  const onAdmin = typeof window !== "undefined" && window.location.pathname.startsWith("/admin");

  // Auto-apply the deal's promo once (silently) so the discount is pre-loaded in the cart
  useEffect(() => {
    if (deal?.active && !applied.current && !promo) {
      applied.current = true;
      applyPromo(deal.code, { silent: true });
    }
  }, [deal, promo, applyPromo]);

  if (!deal?.active || dismissed || onAdmin) return null;
  if (deal.until && left === 0) return null;

  const hasTimer = deal.until && left !== null;
  const d = hasTimer ? Math.floor(left / 86400000) : 0;
  const h = hasTimer ? Math.floor((left % 86400000) / 3600000) : 0;
  const m = hasTimer ? Math.floor((left % 3600000) / 60000) : 0;
  const s = hasTimer ? Math.floor((left % 60000) / 1000) : 0;

  const dismiss = () => {
    setDismissed(true);
    localStorage.setItem("mx_deal_dismissed", `${deal.code}:${deal.until}`);
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
        transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        className="relative z-[60] bg-gradient-to-r from-[#D4AF37] via-[#E9C767] to-[#D4AF37] text-black overflow-hidden"
        data-testid="deal-banner">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-2.5 flex items-center justify-center gap-x-4 gap-y-1 flex-wrap text-center">
          <span className="flex items-center gap-2 font-mono text-[11px] sm:text-xs uppercase tracking-[0.15em] font-bold">
            <Tag size={14} className="shrink-0" /> {deal.headline}{deal.label && !(deal.headline || "").toLowerCase().includes(String(deal.label).toLowerCase()) ? ` — ${deal.label}` : ""}
          </span>
          <span className="hidden sm:inline-block h-3 w-px bg-black/30" />
          <span className="font-mono text-[11px] sm:text-xs" data-testid="deal-code">
            Code <span className="font-bold underline underline-offset-2">{deal.code}</span> auto-applied
          </span>
          {hasTimer && (
            <span className="flex items-center gap-1.5 font-mono text-[11px] sm:text-xs font-bold" data-testid="deal-countdown">
              <span className="hidden sm:inline-block h-3 w-px bg-black/30 mr-1.5" />
              Ends in {d > 0 && `${d}d `}{pad(h)}:{pad(m)}:{pad(s)}
            </span>
          )}
        </div>
        <button onClick={dismiss} data-testid="deal-dismiss" aria-label="Dismiss deal"
          className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 hover:bg-black/10 rounded-full transition-colors">
          <X size={15} />
        </button>
      </motion.div>
    </AnimatePresence>
  );
}
